import { isHiddenUsingStyle } from './is-hidden-using-style';
import type { IsHiddenUsingStyleOptions } from './is-hidden-using-style';
import { isNonVisualElement } from './non-visual-elements';
import { isOutOfView } from './is-out-of-view';
import { isFocusable } from './is-focusable';
import { hasTextContent } from './has-text-content';

export type IsVisibleOptions = IsHiddenUsingStyleOptions & {
  /** Whether to check if element is inside the viewport */
  skipCheckingOutOfView?: boolean;
  /** If true, we will check whether an element is outside the boundaries of the current window */
  checkOutOfWindow?: boolean;
  /** Whether to check ancestor's visibility too */
  skipCheckingAncestors?: boolean;
};

/**
 * Determines whether the given element is visible or not
 */
export function isElementVisible(
  element: Node,
  options: IsVisibleOptions = {},
): boolean {
  let skipZeroSizedCheck = false;

  /**
   * Determines whether the given element has 0px width and/or height.
   * Note: element can have 0 height while all of its children are visible,
   * like if these children have fixed/absolute position.
   */
  const isZeroSized = (
    element: Element,
    rect: DOMRect,
    style: CSSStyleDeclaration,
  ) => {
    if (
      skipZeroSizedCheck ||
      // element has width and height
      (rect.width > 0 && rect.height > 0)
    ) {
      return false;
    }

    if (hasTextContent(element)) {
      return false;
    }

    const willHaveAutoScaleIfChildFloating =
      style.position === 'fixed' ||
      style.position === 'absolute' ||
      style.float === 'left' ||
      style.float === 'right' ||
      style.overflowX !== 'visible' ||
      style.overflowY !== 'visible';

    // has auto height if at least one of the child meets the settings below
    for (let i = 0; i < element.children.length; ++i) {
      const child = element.children[i];
      if (isNonVisualElement(child)) {
        continue;
      }

      const childStyle = window.getComputedStyle(child);
      if (isHiddenUsingStyle(child, childStyle, options)) {
        continue;
      }

      if (
        // has a child without changed position or floating
        (childStyle.position !== 'fixed' &&
          childStyle.position !== 'absolute' &&
          childStyle.float !== 'left' &&
          childStyle.float !== 'right') ||
        // has a child with floating, but element uses overflow property
        ((childStyle.float === 'left' || childStyle.float === 'right') &&
          willHaveAutoScaleIfChildFloating)
      ) {
        return false;
      }
    }

    skipZeroSizedCheck = true;
    return true;
  };

  const checkVisibility = (node: Node | Document) => {
    if (node instanceof Document || node instanceof Text) {
      return true;
    } else if (!(node instanceof Element)) {
      return false;
    } else if (isNonVisualElement(node) && !isFocusable(node)) {
      return false;
    }

    const style = window.getComputedStyle(node);

    if (isHiddenUsingStyle(node, style, options)) {
      return false;
    }

    /**
     * If element's display is 'contents' then:
     * - every number in the result of getBoundingClientRect() equals to 0
     * - getClientRects() returns an empty array
     * - offsetWidth and offsetHeight equals to 0
     * This means that, it needs to skip checking visibility on the element,
     * because the checks below gives invalid result. Also if this element
     * has no visible child, the element is invisible.
     * See https://developer.mozilla.org/en-US/docs/Web/CSS/display#contents
     */
    if (style.display === 'contents') {
      // if it is currently checking an ancestor of the element (aka element
      // passed to `isElementVisible`), we know that node has at least 1 visible child
      if (!(element instanceof Element) || node !== element) {
        return true;
      }

      // checks if element has at least 1 visible child
      for (let i = 0; i < element.children.length; ++i) {
        if (
          isElementVisible(element.children[i], { skipCheckingAncestors: true })
        ) {
          return true;
        }
      }

      return false;
    }

    const rect = node.getBoundingClientRect();

    // scaled to zero
    if (isZeroSized(node, rect, style)) {
      return false;
    }

    // out of view
    if (
      !options.skipCheckingOutOfView &&
      isOutOfView(node, rect, options.checkOutOfWindow)
    ) {
      return false;
    }

    return (
      node.getClientRects().length > 0 ||
      (node instanceof HTMLElement &&
        (node.offsetWidth > 0 || node.offsetHeight > 0))
    );
  };

  if (options.skipCheckingAncestors) {
    return checkVisibility(element);
  }
  // checks visibility of the ancestors of the given element
  else {
    let visible = true;
    let node: Node | null = element;
    while (node && node !== document.body && visible) {
      visible = checkVisibility(node);
      node = node.parentNode;
    }

    return visible;
  }
}
